import { LineSegment2, Point2 } from 'src/domain/geometry/geometric-types';
import { SheetShapeDeepFragment } from 'src/gql/graphql';
import { kilerMap } from 'src/components/orderContractor/ProductChoices';
import {
  onlyOneSide,
  kileSelector,
} from 'src/components/orderContractor/pdf-shapes/protan/kile-selector';
import { findClosestParallelEdgeToSegment } from 'src/components/orderContractor/pdf-shapes/protan/edge-detection';
import { selectWedgeType } from 'src/components/orderContractor/pdf-shapes/protan/wedge-calculations';
import {
  KileTypeSKU,
  Diamond,
} from 'src/components/orderContractor/pdf-shapes/protan/wedge-types';
import { projectPointOnSegment } from 'src/components/orderContractor/shapes/util';

export interface WedgeCalculationResult {
  diamonds: Diamond[];
  kileTypes: KileTypeSKU[];
  segmentMidpoints: [number, number][];
  segments: LineSegment2[];
  points: Point2[];
}

/**
 * Converts a polyline (array of points) into segments
 */
export function polylineToSegments(points: Point2[]): LineSegment2[] {
  const segments: LineSegment2[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    segments.push([points[i], points[i + 1]]);
  }
  return segments;
}

/**
 * Calculates diamond configuration for each segment based on position (first, middle, last)
 */
export function calculateBaseDiamonds(segments: LineSegment2[]): Diamond[] {
  return segments.map((_, index) => {
    if (index === 0) {
      // First segment: triangle1 and triangle3 should be false (only second triangles visible)
      return {
        triangle1: false,
        triangle2: true,
        triangle3: false,
        triangle4: true,
      };
    } else if (index === segments.length - 1) {
      // Last segment: triangle2 and triangle4 should be false (only first triangles visible)
      return {
        triangle1: true,
        triangle2: false,
        triangle3: true,
        triangle4: false,
      };
    } else {
      // Middle segments: all triangles visible
      return {
        triangle1: true,
        triangle2: true,
        triangle3: true,
        triangle4: true,
      };
    }
  });
}

/**
 * Adjusts diamond configuration based on proximity to parallel edges
 */
export function adjustDiamondsForEdgeProximity(
  segments: LineSegment2[],
  baseDiamonds: Diamond[],
  renderedSheetShapes: Record<string, SheetShapeDeepFragment[]>,
  unitScale: number
): Diamond[] {
  const adjustedDiamonds = baseDiamonds.map((diamond, index) => {
    const edgeResult = findClosestParallelEdgeToSegment(
      segments[index],
      renderedSheetShapes,
      0.1,
      unitScale
    );

    // If no closest edge found, return the base diamond
    if (!edgeResult || !edgeResult.isCloserThan100mm) return diamond;

    // Create a copy of the diamond to modify
    const adjustedDiamond = { ...diamond };

    // Only modify diamonds for edges closer than 1m
    if (index === 0) {
      // First segment: only modify triangle2 and triangle4 based on edge position
      if (edgeResult.isOnLeftSide) {
        adjustedDiamond.triangle2 = false;
      } else {
        adjustedDiamond.triangle4 = false;
      }
    } else if (index === segments.length - 1) {
      // Last segment: only modify triangle1 and triangle3 based on edge position
      if (edgeResult.isOnLeftSide) {
        adjustedDiamond.triangle1 = false;
      } else {
        adjustedDiamond.triangle3 = false;
      }
    } else {
      // Middle segments: modify all triangles based on edge position
      if (edgeResult.isOnLeftSide) {
        adjustedDiamond.triangle1 = false;
        adjustedDiamond.triangle2 = false;
      } else {
        adjustedDiamond.triangle3 = false;
        adjustedDiamond.triangle4 = false;
      }
    }

    return adjustedDiamond;
  });

  return adjustedDiamonds;
}

/**
 * Calculates kile types for each segment based on diamond configuration and segment length
 */
export function calculateKileTypes(
  segments: LineSegment2[],
  diamonds: Diamond[],
  unitScale: number
): KileTypeSKU[] {
  return segments.map((segment, index) => {
    const diamond = diamonds[index];

    // Calculate segment length in meters
    const segmentLengthPdf = Math.sqrt(
      Math.pow(segment[1][0] - segment[0][0], 2) +
        Math.pow(segment[1][1] - segment[0][1], 2)
    );
    const segmentLengthMeters = segmentLengthPdf * unitScale;

    // Round to 2 decimal places to avoid floating point issues
    const roundedLength = Math.round(segmentLengthMeters * 100) / 100;

    // Get kile type using the wedge calculation utility
    const kileType = selectWedgeType(diamond, roundedLength);

    // Default to a common type if no valid type returned
    return kileType || KileTypeSKU.RW257379;
  });
}

/**
 * Calculates midpoints for each segment
 */
export function calculateSegmentMidpoints(
  segments: LineSegment2[]
): [number, number][] {
  return segments.map((segment) => [
    (segment[0][0] + segment[1][0]) / 2,
    (segment[0][1] + segment[1][1]) / 2,
  ]);
}

/**
 * Main function to calculate complete wedge configuration from a polyline
 */
export function calculateWedgeConfiguration(
  points: Point2[],
  renderedSheetShapes: Record<string, SheetShapeDeepFragment[]>,
  unitScale: number | null
): WedgeCalculationResult {
  // Convert points to segments
  const segments = polylineToSegments(points);

  // Generate base diamonds for each segment
  const baseDiamonds = calculateBaseDiamonds(segments);

  // Adjust diamonds based on edge detection if we have the required data
  const diamonds =
    unitScale && renderedSheetShapes
      ? adjustDiamondsForEdgeProximity(
          segments,
          baseDiamonds,
          renderedSheetShapes,
          unitScale
        )
      : baseDiamonds;

  // Calculate kile types for each segment
  const kileTypes = unitScale
    ? calculateKileTypes(segments, diamonds, unitScale)
    : [];

  // Calculate midpoints for each segment
  const segmentMidpoints = calculateSegmentMidpoints(segments);

  return {
    diamonds,
    kileTypes,
    segmentMidpoints,
    segments,
    points,
  };
}

/**
 * Creates diamond shapes around a segment for rendering
 */
export function createDiamondAroundSegment(
  segment: LineSegment2,
  diamondPlacement: 'ONESIDE' | 'CENTER'
): Point2[][] {
  const [start, end] = segment;

  // Calculate the midpoint of the line segment
  const midPoint: Point2 = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];

  // Calculate the distance between start and end points (length of the segment)
  const length = Math.sqrt((end[0] - start[0]) ** 2 + (end[1] - start[1]) ** 2);

  // Calculate the angle of the segment
  const angle = Math.atan2(end[1] - start[1], end[0] - start[0]);

  const widthLengthFactorSingle = 3600 / 14400;
  const widthLengthFactorDouble = 3600 / (14400 * 2);

  if (diamondPlacement === 'ONESIDE') {
    const trianglePoints: Point2[] = [
      [end[0], end[1]],
      [start[0], start[1]],
      [
        end[0] +
          length * widthLengthFactorSingle * Math.cos(angle + Math.PI / 2),
        end[1] +
          length * widthLengthFactorSingle * Math.sin(angle + Math.PI / 2),
      ],
    ];
    const trianglePoints2: Point2[] = [
      [start[0], start[1]],
      [end[0], end[1]],
      [
        start[0] +
          length * widthLengthFactorSingle * Math.cos(angle + Math.PI / 2),
        start[1] +
          length * widthLengthFactorSingle * Math.sin(angle + Math.PI / 2),
      ],
    ];

    const trianglePoints3: Point2[] = [
      [end[0], end[1]],
      [start[0], start[1]],
      [
        end[0] +
          length *
            widthLengthFactorSingle *
            Math.cos(angle + (Math.PI * 2 * 3) / 4),
        end[1] +
          length *
            widthLengthFactorSingle *
            Math.sin(angle + (Math.PI * 2 * 3) / 4),
      ],
    ];
    const trianglePoints4: Point2[] = [
      [start[0], start[1]],
      [end[0], end[1]],
      [
        start[0] +
          length *
            widthLengthFactorSingle *
            Math.cos(angle + (Math.PI * 2 * 3) / 4),
        start[1] +
          length *
            widthLengthFactorSingle *
            Math.sin(angle + (Math.PI * 2 * 3) / 4),
      ],
    ];

    return [trianglePoints, trianglePoints2, trianglePoints3, trianglePoints4];
  }

  // Calculate the coordinates of the diamond points for CENTER placement
  const trianglePoints: Point2[] = [
    [midPoint[0], midPoint[1]],
    [start[0], start[1]],
    [
      midPoint[0] +
        length * widthLengthFactorDouble * Math.cos(angle + Math.PI / 2),
      midPoint[1] +
        length * widthLengthFactorDouble * Math.sin(angle + Math.PI / 2),
    ],
  ];
  const trianglePoints2: Point2[] = [
    [midPoint[0], midPoint[1]],
    [end[0], end[1]],
    [
      midPoint[0] +
        length * widthLengthFactorDouble * Math.cos(angle + Math.PI / 2),
      midPoint[1] +
        length * widthLengthFactorDouble * Math.sin(angle + Math.PI / 2),
    ],
  ];

  const trianglePoints3: Point2[] = [
    [midPoint[0], midPoint[1]],
    [start[0], start[1]],
    [
      midPoint[0] +
        length *
          widthLengthFactorDouble *
          Math.cos(angle + (Math.PI * 2 * 3) / 4),
      midPoint[1] +
        length *
          widthLengthFactorDouble *
          Math.sin(angle + (Math.PI * 2 * 3) / 4),
    ],
  ];
  const trianglePoints4: Point2[] = [
    [midPoint[0], midPoint[1]],
    [end[0], end[1]],
    [
      midPoint[0] +
        length *
          widthLengthFactorDouble *
          Math.cos(angle + (Math.PI * 2 * 3) / 4),
      midPoint[1] +
        length *
          widthLengthFactorDouble *
          Math.sin(angle + (Math.PI * 2 * 3) / 4),
    ],
  ];

  return [trianglePoints, trianglePoints2, trianglePoints3, trianglePoints4];
}

/**
 * Calculates product lengths for segments based on their diamond configuration
 */
export function calculateProductLengths(
  segments: LineSegment2[],
  diamonds: Diamond[],
  unitScale: number
): number[] {
  return segments.map((segment, segmentIndex) => {
    const segmentDiamond = diamonds[segmentIndex];

    if (!segmentDiamond) return 0;

    // Calculate segment length in meters
    const segmentLengthPdf = Math.sqrt(
      Math.pow(segment[1][0] - segment[0][0], 2) +
        Math.pow(segment[1][1] - segment[0][1], 2)
    );
    const segmentLengthMeters = segmentLengthPdf * unitScale;
    const roundedSegmentLength = Math.round(segmentLengthMeters * 100) / 100;

    // Consider the diamond configuration when determining effective segment length
    const isTwoSided = !onlyOneSide(segmentDiamond);
    const effectiveLength = isTwoSided
      ? roundedSegmentLength / 2
      : roundedSegmentLength;

    // Get the wedge type for this segment
    const segmentWedgeType = kileSelector(
      segmentDiamond,
      effectiveLength
    ) as KileTypeSKU;

    return segmentWedgeType ? kilerMap[segmentWedgeType][0]?.length || 0 : 0;
  });
}

/**
 * Creates a wedge label based on product length
 */
export function createWedgeLabel(productLength: number): string {
  return `${productLength.toFixed(1)}m`;
}

/**
 * Calculates rotation angle from line segment direction for labels
 */
export function calculateRotationFromSegment(
  segment: LineSegment2,
  pdfRotation?: number
): number {
  const [start, end] = segment;

  // Calculate direction vector
  const directionVector: Point2 = [end[0] - start[0], end[1] - start[1]];

  // Calculate rotation angle from direction vector
  const angleRad = Math.atan2(directionVector[1], directionVector[0]);
  let angleDeg = angleRad * (180 / Math.PI);

  // Normalize the angle to -180 to 180 range
  if (angleDeg > 180) angleDeg -= 360;
  if (angleDeg < -180) angleDeg += 360;

  // Apply PDF rotation correction if provided
  if (pdfRotation !== undefined) {
    angleDeg += pdfRotation;
    // Normalize again after adding PDF rotation
    if (angleDeg > 180) angleDeg -= 360;
    if (angleDeg < -180) angleDeg += 360;
  }

  // Ensure text is right-side up (readable)
  if (angleDeg > 90 || angleDeg < -90) {
    angleDeg += 180;
    if (angleDeg > 180) angleDeg -= 360;
  }

  // Invert the angle to fix the rotation direction
  angleDeg = -angleDeg;

  return angleDeg;
}

// --- Shared wedge drag handlers (imported from V1) ---
export const handleStartPointDrag = (
  newPoint: Point2,
  points: Point2[],
  recalculateWedgePoints: (
    startPoint: Point2,
    endPoint: Point2
  ) => WedgeCalculationResult | null
) => {
  const result = recalculateWedgePoints(newPoint, points[points.length - 1]);
  if (!result || !('points' in result)) return null;
  return {
    wedgePoints: (result as WedgeCalculationResult).points,
    wedgeKileTypes: result.kileTypes,
    newDiamonds: result.diamonds,
  };
};

export const handleEndPointDrag = (
  newPoint: Point2,
  points: Point2[],
  recalculateWedgePoints: (
    startPoint: Point2,
    endPoint: Point2
  ) => WedgeCalculationResult | null
) => {
  const result = recalculateWedgePoints(points[0], newPoint);
  if (!result || !('points' in result)) return null;
  return {
    wedgePoints: (result as WedgeCalculationResult).points,
    wedgeKileTypes: result.kileTypes,
    newDiamonds: result.diamonds,
  };
};

export const handleMiddlePointDrag = (
  newPoint: Point2,
  middleIndex: number,
  points: Point2[],
  diamonds: Diamond[],
  kileTypes: KileTypeSKU[],
  unitScale: number
) => {
  if (
    middleIndex < points.length &&
    middleIndex > 0 &&
    middleIndex < points.length - 1 &&
    unitScale
  ) {
    const newPoints = [...points];
    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];
    const constrainedPoint = projectPointOnSegment(newPoint, [
      firstPoint,
      lastPoint,
    ]);

    // Check for collapsing with other points
    const collapseResult = checkAndCollapsePoints(
      constrainedPoint,
      middleIndex,
      newPoints,
      diamonds,
      kileTypes,
      unitScale
    );

    if (collapseResult) {
      // Points were collapsed, return the collapsed result
      return collapseResult;
    }

    // No collapsing occurred, update normally
    newPoints[middleIndex] = constrainedPoint;
    // Update affected segments
    const { newKileTypes, newDiamonds } = updateSegmentsForMiddlePoint(
      middleIndex,
      constrainedPoint,
      points,
      diamonds,
      kileTypes,
      unitScale
    );
    return { newPoints, newKileTypes, newDiamonds };
  }
  return null;
};

function updateSegmentsForMiddlePoint(
  middleIndex: number,
  constrainedPoint: Point2,
  points: Point2[],
  diamonds: Diamond[],
  kileTypes: KileTypeSKU[],
  unitScale: number
) {
  const newKileTypes = [...kileTypes];
  const newDiamonds = [...diamonds];
  const segmentsBefore = middleIndex - 1;
  const segmentsAfter = middleIndex;
  if (segmentsBefore >= 0 && unitScale) {
    const segmentBeforeLength = Math.sqrt(
      Math.pow(constrainedPoint[0] - points[middleIndex - 1][0], 2) +
        Math.pow(constrainedPoint[1] - points[middleIndex - 1][1], 2)
    );
    const segmentBeforeLengthMeters = segmentBeforeLength * unitScale;
    let segmentBeforeDiamond: Diamond;
    if (segmentsBefore === 0) {
      segmentBeforeDiamond = {
        triangle1: false,
        triangle2: true,
        triangle3: false,
        triangle4: true,
      };
    } else {
      segmentBeforeDiamond = {
        triangle1: true,
        triangle2: true,
        triangle3: true,
        triangle4: true,
      };
    }
    newDiamonds[segmentsBefore] = segmentBeforeDiamond;
    newKileTypes[segmentsBefore] = selectWedgeType(
      segmentBeforeDiamond,
      segmentBeforeLengthMeters
    );
  }
  if (segmentsAfter < points.length - 1 && unitScale) {
    const segmentAfterLength = Math.sqrt(
      Math.pow(points[middleIndex + 1][0] - constrainedPoint[0], 2) +
        Math.pow(points[middleIndex + 1][1] - constrainedPoint[1], 2)
    );
    const segmentAfterLengthMeters = segmentAfterLength * unitScale;
    let segmentAfterDiamond: Diamond;
    if (segmentsAfter === points.length - 2) {
      segmentAfterDiamond = {
        triangle1: true,
        triangle2: false,
        triangle3: true,
        triangle4: false,
      };
    } else {
      segmentAfterDiamond = {
        triangle1: true,
        triangle2: true,
        triangle3: true,
        triangle4: true,
      };
    }
    newDiamonds[segmentsAfter] = segmentAfterDiamond;
    newKileTypes[segmentsAfter] = selectWedgeType(
      segmentAfterDiamond,
      segmentAfterLengthMeters
    );
  }
  return { newKileTypes, newDiamonds };
}

/**
 * Finds the closest point within the proximity threshold
 */
function findClosestPointForCollapse(
  draggedPoint: Point2,
  draggedIndex: number,
  points: Point2[],
  unitScale: number
): number | null {
  const proximityThresholdMeters = 0.1; // 100mm
  const proximityThresholdPdf = proximityThresholdMeters / unitScale;

  let collapseTargetIndex: number | null = null;
  let minDistance = Infinity;

  for (let i = 0; i < points.length; i++) {
    if (i === draggedIndex) continue;

    const distance = Math.sqrt(
      Math.pow(draggedPoint[0] - points[i][0], 2) +
        Math.pow(draggedPoint[1] - points[i][1], 2)
    );

    if (distance < proximityThresholdPdf && distance < minDistance) {
      collapseTargetIndex = i;
      minDistance = distance;
    }
  }

  return collapseTargetIndex;
}

/**
 * Creates a diamond configuration based on segment position
 */
function createDiamondForSegment(
  segmentIndex: number,
  totalSegments: number
): Diamond {
  if (totalSegments === 1) {
    // Only one segment - same as first segment (apex at endpoint)
    return {
      triangle1: false,
      triangle2: true,
      triangle3: false,
      triangle4: true,
    };
  } else if (segmentIndex === 0) {
    // First segment
    return {
      triangle1: false,
      triangle2: true,
      triangle3: false,
      triangle4: true,
    };
  } else if (segmentIndex === totalSegments - 1) {
    // Last segment
    return {
      triangle1: true,
      triangle2: false,
      triangle3: true,
      triangle4: false,
    };
  } else {
    // Middle segment
    return {
      triangle1: true,
      triangle2: true,
      triangle3: true,
      triangle4: true,
    };
  }
}

/**
 * Checks if a dragged midpoint should collapse with nearby points and performs the collapse if needed
 */
function checkAndCollapsePoints(
  draggedPoint: Point2,
  draggedIndex: number,
  points: Point2[],
  diamonds: Diamond[],
  kileTypes: KileTypeSKU[],
  unitScale: number
): {
  newPoints: Point2[];
  newKileTypes: KileTypeSKU[];
  newDiamonds: Diamond[];
} | null {
  const collapseTargetIndex = findClosestPointForCollapse(
    draggedPoint,
    draggedIndex,
    points,
    unitScale
  );

  if (collapseTargetIndex === null) {
    return null;
  }

  // Perform the collapse
  const newPoints = [...points];
  const newDiamonds = [...diamonds];
  const newKileTypes = [...kileTypes];

  // Remove the dragged point
  newPoints.splice(draggedIndex, 1);

  // When we remove a point, we also remove one segment (the one that connected to the removed point)
  // We need to remove the diamond and kile type for the segment that's being removed
  // The segment to remove depends on which point we're collapsing into

  let segmentToRemove: number;
  if (collapseTargetIndex < draggedIndex) {
    // Collapsing into a point to the left - remove the segment before the dragged point
    segmentToRemove = draggedIndex - 1;
  } else {
    // Collapsing into a point to the right - remove the segment after the dragged point
    segmentToRemove = draggedIndex;
  }

  // Remove the corresponding diamond and kile type
  newDiamonds.splice(segmentToRemove, 1);
  newKileTypes.splice(segmentToRemove, 1);

  // Special case: if we're collapsing to the startpoint and will have only one segment,
  // we need to flip the points so the startpoint becomes the endpoint
  if (collapseTargetIndex === 0 && newPoints.length === 2) {
    // Flip the points so the startpoint becomes the endpoint
    const temp = newPoints[0];
    newPoints[0] = newPoints[1];
    newPoints[1] = temp;
  }

  // Now we need to recalculate all diamonds and kile types for the new configuration
  // Create segments from the new points
  const segments: LineSegment2[] = [];
  for (let i = 0; i < newPoints.length - 1; i++) {
    segments.push([newPoints[i], newPoints[i + 1]]);
  }

  // Recalculate all diamonds based on the new segments
  const recalculatedDiamonds: Diamond[] = [];
  const recalculatedKileTypes: KileTypeSKU[] = [];

  segments.forEach((segment, segmentIndex) => {
    const segmentLength = Math.sqrt(
      Math.pow(segment[1][0] - segment[0][0], 2) +
        Math.pow(segment[1][1] - segment[0][1], 2)
    );
    const segmentLengthMeters = segmentLength * unitScale;

    const diamond = createDiamondForSegment(segmentIndex, segments.length);
    recalculatedDiamonds.push(diamond);
    recalculatedKileTypes.push(selectWedgeType(diamond, segmentLengthMeters));
  });

  return {
    newPoints,
    newKileTypes: recalculatedKileTypes,
    newDiamonds: recalculatedDiamonds,
  };
}
